import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { PendingPriceChange } from "@/api/types";

import i18n from "@/i18n";

/** Billing types mirror `app/api/org_billing.py`.
 *
 *  Money crosses the wire as INTEGER MINOR UNITS (öre/cent) and is only
 *  ever split into a decimal at the moment of rendering — see
 *  `formatMinor` in `@/features/admin/money`, the one formatter.
 *  Credits stay in USD-micros and are a different unit entirely; nothing
 *  here converts between the two. */

export type BillingAgreement = {
  id: string;
  status: "draft" | "active" | "past_due" | "canceled";
  provider: string;
  currency: string;
  plan_slug: string | null;
  plan_name: string | null;
  plan_credits_micros: number;
  /** "Beta" / "Not enrolled": never billed and never sold. Optional so
   *  an older API response (or an existing test fixture) that omits it
   *  is treated as a normal, billable plan. */
  plan_is_internal?: boolean;
  /** Users the plan itself includes. Optional for older responses. */
  plan_included_seats?: number;
  /** "month" | "year" — what "per …" this agreement's prices mean. */
  plan_interval?: string | null;
  /** What THIS customer pays per month for the plan — their own,
   *  possibly grandfathered price, not today's list price. */
  plan_unit_amount_minor: number;
  /** Today's list price for the plan; null for an unsold plan. Optional
   *  for older responses. */
  plan_list_unit_amount_minor?: number | null;
  plan_price_differs?: boolean;
  /** The customer's own price per extra user, and today's list price. */
  seat_unit_amount_minor?: number | null;
  seat_list_unit_amount_minor?: number | null;
  seat_price_differs?: boolean;
  /** The next period's price where it differs from this period's (a
   *  scheduled downgrade or price change), and from when. */
  plan_next_unit_amount_minor?: number | null;
  seat_next_unit_amount_minor?: number | null;
  next_price_from?: string | null;
  /** A scheduled move to today's list price, or null. */
  price_change?: PendingPriceChange | null;
  seat_quantity: number;
  scheduled_seat_quantity: number | null;
  scheduled_plan_slug: string | null;
  /** Optional so an older API response that omits it falls back to the slug. */
  scheduled_plan_name?: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
};

export type OrgBillingSummary = {
  org_slug: string;
  agreement: BillingAgreement | null;
  measured_active_users: number;
  default_currency: string;
  provider: string;
  /** More members than the plan covers after a capacity cut: new work
   *  continues until this moment, then stops. Null when within capacity. */
  seat_grace_until?: string | null;
};

export type BillingPlanRow = {
  slug: string;
  name: string;
  description: string | null;
  kind: "subscription" | "seat" | "credit_pack";
  interval: string | null;
  credits_micros: number;
  currency: string;
  unit_amount_minor: number;
  /** User licences the plan price already covers. */
  included_seats: number;
  /** Cost of one licence beyond those included, in minor units. `null`
   *  when this plan prices no seats in the org's currency — render
   *  nothing, never "0 / user", which reads as unlimited free users. */
  seat_unit_amount_minor: number | null;
};

export type BillingInvoiceLineRow = {
  kind: string;
  description: string;
  quantity: number;
  unit_amount_minor: number;
  amount_minor: number;
  credits_granted_micros: number;
};

/** A reversal of a settled invoice.
 *
 *  Its own document with its own number and its own issue date — not a
 *  status on the invoice it credits. A paid invoice stays paid; erasing
 *  it would claim the sale never happened. */
export type BillingCreditNoteRow = {
  id: string;
  number: string;
  currency: string;
  subtotal_minor: number;
  tax_minor: number;
  total_minor: number;
  issued_at: string | null;
  reason: string | null;
  hosted_url: string | null;
};

export type BillingInvoiceRow = {
  id: string;
  number: string;
  status: string;
  provider: string;
  currency: string;
  subtotal_minor: number;
  tax_minor: number;
  total_minor: number;
  issued_at: string | null;
  paid_at: string | null;
  period_start: string | null;
  period_end: string | null;
  hosted_url: string | null;
  credit_notes: BillingCreditNoteRow[];
  lines: BillingInvoiceLineRow[];
};

/** Pull the inline-renderable message out of a structured billing
 *  failure — the 502 `billing_provider_error` (Stripe refused) and the
 *  422 `billing_error` (a typed service refusal such as
 *  `price_not_found`). Returns null for anything else so the caller can
 *  fall back to its own copy.
 *
 *  `code` is the fallback because a typed refusal always carries one
 *  even when the message is empty; rendering the code beats rendering
 *  nothing next to the control the admin just used. */
/** Provider/service codes an org admin can actually reach, in their
 *  language rather than the operator's. Anything absent falls through to
 *  the backend string, which is the right default for codes only an
 *  operator sees. */
// Codes → i18n keys under billing.errors.* (en/sv/fi in the locale
// files). Absent codes fall through to the backend string — right for
// codes only an operator sees.
const CUSTOMER_BILLING_ERROR_CODES: readonly string[] = [
  "no_stripe_subscription",
  "price_not_found",
  "subscription_required",
  // Lifecycle refusals (422 with a bare code). These used to render
  // as nothing at all: a click that did nothing, with no explanation.
  "agreement_past_due",
  "org_already_has_live_agreement",
  "plan_inactive",
  "plan_not_subscription",
  "negative_seat_quantity",
  "agreement_not_live",
  // A platform admin who isn't an admin of the org pressed a buy button.
  "billing_self_serve_only",
];

function customerBillingError(code: string): string | null {
  if (!CUSTOMER_BILLING_ERROR_CODES.includes(code)) return null;
  return i18n.t(`billing.errors.${code}`);
}

export function billingErrorMessage(err: unknown): string | null {
  const detail = (
    err as { response?: { data?: { detail?: unknown } } } | undefined
  )?.response?.data?.detail;
  if (typeof detail === "string") {
    return customerBillingError(detail) ?? detail;
  }
  if (detail && typeof detail === "object") {
    const shaped = detail as { reason?: string; code?: string; message?: string };
    // A refusal whose reason is itself a customer code (the past-due and
    // plan-required guards) reads the same as the bare-code form. Without
    // this the BillingTab showed nothing at all for them.
    const byReason = customerBillingError(shaped.reason ?? "");
    if (byReason) return byReason;
    if (shaped.reason === "cancel_pending_after_refund") {
      return i18n.t(
        "admin.billing.error_cancel_pending",
        "Your subscription is being ended after a refund, so it can no longer be changed. Contact support if this is unexpected.",
      );
    }
    if (
      shaped.reason === "billing_provider_error" ||
      shaped.reason === "billing_error"
    ) {
      // Codes an org ADMIN can hit are translated to what they can do
      // about it. The raw strings are written for whoever operates the
      // platform — "the agreement has no Stripe subscription" tells a
      // paying customer nothing they can act on, and it is the reply
      // to a button they just pressed.
      const friendly = customerBillingError(shaped.code ?? "");
      if (friendly) return friendly;
      return shaped.message ?? shaped.code ?? null;
    }
  }
  return null;
}

export function useOrgBilling(slug: string) {
  return useQuery({
    queryKey: ["org", "billing", slug],
    // An empty slug means no organisation is selected. Without this
    // the hook requests "/orgs//billing", which is a malformed
    // path that 404s on every render of the all-orgs view.
    enabled: Boolean(slug),
    queryFn: async () =>
      (await api.get<OrgBillingSummary>(`/orgs/${slug}/billing`)).data,
    retry: false,
    staleTime: 30_000,
  });
}

export function useBillingPlans(slug: string, kind?: BillingPlanRow["kind"]) {
  return useQuery({
    queryKey: ["org", "billing", "plans", slug, kind ?? "all"],
    // An empty slug means no organisation is selected. Without this
    // the hook requests "/orgs//billing", which is a malformed
    // path that 404s on every render of the all-orgs view.
    enabled: Boolean(slug),
    queryFn: async () =>
      (
        await api.get<{ currency: string; plans: BillingPlanRow[] }>(
          kind
            ? `/orgs/${slug}/billing/plans?kind=${kind}`
            : `/orgs/${slug}/billing/plans`,
        )
      ).data,
    retry: false,
    staleTime: 60_000,
  });
}

/** What choosing a plan would do to the org's users, from
 *  `GET /orgs/{slug}/billing/plan-preview`. The server applies the same
 *  rules as the change itself, so nothing here is recomputed. */
export type PlanChangePreview = {
  plan_slug: string;
  change: "current" | "checkout" | "upgrade" | "downgrade";
  /** When a downgrade lands; null when the change applies now. */
  effective_at: string | null;
  members: number;
  /** Licences issued today. A plan change never removes one. */
  licensed: number;
  /** User limit now and after the change; null = no limit. */
  capacity_before: number | null;
  capacity_after: number | null;
  declared_seats_after: number;
  /** Declared licences above what the new plan includes, billed as
   *  extra users. */
  billed_extra_seats_after: number;
  /** More members than the new limit: new work stops org-wide. */
  new_work_blocked_after: boolean;
  /** Why the change would be refused right now (e.g.
   *  "agreement_past_due"), or null. */
  refusal?: string | null;
};

export function usePlanPreview(slug: string, planSlug: string | null) {
  return useQuery({
    queryKey: ["org", "billing", slug, "plan-preview", planSlug],
    enabled: Boolean(slug) && Boolean(planSlug),
    queryFn: async () =>
      (
        await api.get<PlanChangePreview>(`/orgs/${slug}/billing/plan-preview`, {
          params: { plan: planSlug },
        })
      ).data,
    retry: false,
  });
}

export function useOrgInvoices(slug: string) {
  return useQuery({
    queryKey: ["org", "billing", "invoices", slug],
    // An empty slug means no organisation is selected. Without this
    // the hook requests "/orgs//billing", which is a malformed
    // path that 404s on every render of the all-orgs view.
    enabled: Boolean(slug),
    queryFn: async () =>
      (await api.get<{ invoices: BillingInvoiceRow[] }>(
        `/orgs/${slug}/billing/invoices`,
      )).data.invoices,
    retry: false,
  });
}

export function useChangePlan(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (planSlug: string) =>
      (
        // ``url`` is present only when the backend needs payment collected
        // first: on a Stripe deploy a new subscription (and an upgrade that
        // has to be charged) comes back as {applied:"checkout", url} and the
        // caller MUST redirect. Manual deploys and scheduled downgrades
        // return no url, so the caller must treat it as optional.
        await api.post<{ applied: string; plan_slug: string; url?: string }>(
          `/orgs/${slug}/billing/plan`,
          { plan_slug: planSlug },
        )
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", "billing", slug] });
    },
  });
}

export function useChangeSeats(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (seatQuantity: number) =>
      (
        await api.post<{ applied: string; seat_quantity: number }>(
          `/orgs/${slug}/billing/seats`,
          { seat_quantity: seatQuantity },
        )
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", "billing", slug] });
      // The licence panel reads CAPACITY from its own query. Refreshing
      // only the billing one left the summary saying "7 covered by your
      // plan" directly above a cost line for 9 — the two halves of one
      // panel disagreeing about what was just bought.
      void queryClient.invalidateQueries({ queryKey: ["org", "licences", slug] });
    },
  });
}

/** Stripe's own billing-management page: card updates, invoice history,
 *  and self-serve cancellation.
 *
 *  The endpoint existed with no caller, so the `past_due` banner told a
 *  customer to update their payment method and rendered no control. An
 *  expired card, or an SCA challenge on an off-session renewal, was
 *  unrecoverable in-app for a paying customer — support email was the only
 *  route, which is exactly what the endpoint was written to end. */
export function useOpenBillingPortal(slug: string) {
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ url: string }>(`/orgs/${slug}/billing/portal`, {})).data,
  });
}

export function useStartCheckout(slug: string) {
  return useMutation({
    mutationFn: async ({
      planSlug,
      returnSessionId,
    }: {
      planSlug: string;
      /** Chat session the purchase started from; Checkout returns the
       *  buyer there instead of Admin Billing. Validated server-side. */
      returnSessionId?: string;
    }) =>
      (
        await api.post<{ url: string }>(`/orgs/${slug}/billing/checkout`, {
          plan_slug: planSlug,
          return_session_id: returnSessionId ?? null,
        })
      ).data,
  });
}

// ---------------------------------------------------------------- licences

/** One member of the org and whatever licence state they hold.
 *
 *  The listing returns every MEMBER, licensed or not: an admin assigning
 *  licences needs to see who does not have one. */
export type LicenceMember = {
  user_id: string;
  email: string;
  display_name: string | null;
  role: string;
  licensed: boolean;
  /** null = uncapped. Only enforced when the org is in "allocated" mode. */
  quota_credits: number | null;
  spent_credits: number;
  assigned_at: string | null;
};

export type LicenceList = {
  members: LicenceMember[];
  /** Licences ISSUED — not the declared count the invoice follows. */
  licensed_count: number;
  /** null = unlimited (an internal plan). */
  capacity: number | null;
  /** Issued exceeds capacity. Reachable after the 0078 backfill, which
   *  licensed every existing member rather than picking who to cut off. */
  over_capacity: boolean;
  quota_mode: "shared" | "allocated";
  period_start: string;
  period_end: string;
  micros_per_credit: number;
};

export function useOrgLicences(slug: string) {
  return useQuery({
    queryKey: ["org", "licences", slug],
    enabled: Boolean(slug),
    queryFn: async () =>
      (await api.get<LicenceList>(`/orgs/${slug}/licences`)).data,
    retry: false,
    staleTime: 15_000,
  });
}

/** Invalidate everything a licence change can move.
 *
 *  Assigning a licence changes the listing AND the billing summary's
 *  seat figures, so refreshing only the first leaves the plan card
 *  showing a stale count on the same screen. */
function useLicenceInvalidation(slug: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["org", "licences", slug] });
    void queryClient.invalidateQueries({ queryKey: ["org", "billing", slug] });
  };
}

export function useAssignLicence(slug: string) {
  const invalidate = useLicenceInvalidation(slug);
  return useMutation({
    mutationFn: async (input: { userId: string; quotaCredits?: number | null }) =>
      (
        await api.post<{ ok: boolean }>(`/orgs/${slug}/licences`, {
          user_id: input.userId,
          quota_credits: input.quotaCredits ?? null,
        })
      ).data,
    onSuccess: invalidate,
  });
}

export function useSetLicenceQuota(slug: string) {
  const invalidate = useLicenceInvalidation(slug);
  return useMutation({
    mutationFn: async (input: { userId: string; quotaCredits: number | null }) =>
      (
        await api.patch<{ ok: boolean }>(
          `/orgs/${slug}/licences/${input.userId}`,
          { quota_credits: input.quotaCredits },
        )
      ).data,
    onSuccess: invalidate,
  });
}

export function useRevokeLicence(slug: string) {
  const invalidate = useLicenceInvalidation(slug);
  return useMutation({
    mutationFn: async (userId: string) =>
      (await api.delete<{ ok: boolean }>(`/orgs/${slug}/licences/${userId}`)).data,
    onSuccess: invalidate,
  });
}

export function useSetQuotaMode(slug: string) {
  const invalidate = useLicenceInvalidation(slug);
  return useMutation({
    mutationFn: async (mode: "shared" | "allocated") =>
      (
        await api.put<{ ok: boolean; mode: string }>(
          `/orgs/${slug}/licences/mode`,
          { mode },
        )
      ).data,
    onSuccess: invalidate,
  });
}
